/**
 * Cached default widget/chat form copy per locale.
 * Overlay only replaces stock (uncustomized) field values so merchant edits stick.
 */

/** Persisted on chat_settings / review_settings — independent of shop UI language. */
export const WIDGET_FORM_LANGUAGE_KEY = "form_language";

export const REVIEW_COPY_FIELDS = [
    "review_button_text",
    "first_review_text",
    "based_on_reviews_text",
    "popup_title_text",
    "popup_rating_excellent_text",
    "popup_rating_just_wow_text",
    "popup_rating_i_like_it_text",
    "popup_rating_i_dont_like_it_text",
    "popup_rating_i_hate_it_text",
    "review_media_gallery_title",
    "review_media_view_all_text",
    "review_trust_1_title",
    "review_trust_1_subtitle",
    "review_trust_2_title",
    "review_trust_2_subtitle",
    "review_trust_3_title",
    "review_trust_3_subtitle",
    "review_cta_title",
    "review_cta_subtitle",
    "review_list_section_title",
    "review_verified_buyer_text",
    "review_reply_from_text",
    "review_store_badge_text",
] as const;

export const CHAT_COPY_FIELDS = [
    "welcome_message",
    "info_message",
    "offline_message",
    "ai_handoff_message",
    "engage_button_label",
] as const;

export type WidgetFormCopyKind = "reviews" | "chats";

/** Backend / legacy English defaults that still count as stock. */
export const STOCK_ALIASES: Record<string, string[]> = {
    review_button_text: ["Write a Review", "Write a review"],
    first_review_text: ["Be the first to write a review", "Be the first to review this product"],
    based_on_reviews_text: ["Based on reviews", "Based on {count} reviews", "based on {count} reviews"],
    popup_title_text: ["HOW WOULD YOU RATE THIS ITEM?", "Write a Review"],
    popup_rating_excellent_text: ["EXCELLENT", "Excellent"],
    popup_rating_just_wow_text: ["JUST WOW", "Just Wow", "Great"],
    popup_rating_i_like_it_text: ["I LIKE IT", "I Like It", "Good"],
    popup_rating_i_dont_like_it_text: ["I DON'T LIKE IT", "I Don't Like It", "Fair"],
    popup_rating_i_hate_it_text: ["I HATE IT", "I Hate It", "Poor"],
    review_media_gallery_title: ["Customer photos & videos"],
    review_media_view_all_text: ["View all"],
    review_trust_1_title: ["Trusted by {count}+ customers", "Trusted by Verified Customers"],
    review_trust_1_subtitle: ["Real reviews from verified buyers"],
    review_trust_2_title: ["Honest feedback"],
    review_trust_2_subtitle: ["Help others make the right choice"],
    review_trust_3_title: ["Share your experience"],
    review_trust_3_subtitle: ["Your review helps our community"],
    review_cta_title: ["Have something to say?"],
    review_cta_subtitle: ["Share your experience and help others"],
    review_list_section_title: ["Customer reviews ({count})"],
    review_verified_buyer_text: ["Verified Buyer"],
    review_reply_from_text: ["Reply from"],
    review_store_badge_text: ["Store"],
    welcome_message: [
        "Please tell us more about you.",
        "Please provide some info so that we can reach you.",
    ],
    info_message: ["Live Chat"],
    offline_message: ["Sorry we're currently unavailable."],
    ai_handoff_message: ["Connecting you with a team member. They will reply shortly."],
    engage_button_label: ["Take over chat", "Talk to our team"],
};

export const copyCache = new Map<string, Record<string, string>>();
const stockSeen = new Map<string, Set<string>>();

export function rememberStock(copy: Record<string, string>): void {
    for (const [field, value] of Object.entries(copy)) {
        if (!value) {
            continue;
        }
        let set = stockSeen.get(field);
        if (!set) {
            set = new Set(STOCK_ALIASES[field] ?? []);
            stockSeen.set(field, set);
        }
        set.add(value);
    }
}

export function isStockCopy(field: string, value: unknown, prevCopy?: Record<string, string>): boolean {
    if (typeof value !== "string") {
        return false;
    }
    const trimmed = value.trim();
    if (trimmed === "") {
        return true;
    }
    if (prevCopy && prevCopy[field] === value) {
        return true;
    }
    const aliases = STOCK_ALIASES[field];
    if (aliases && aliases.includes(value)) {
        return true;
    }
    return stockSeen.get(field)?.has(value) === true;
}

export function overlayStockCopy<T extends Record<string, unknown>>(
    values: T,
    prevCopy: Record<string, string>,
    nextCopy: Record<string, string>
): T {
    const next = { ...values };
    for (const field of Object.keys(nextCopy)) {
        if (isStockCopy(field, values[field], prevCopy)) {
            (next as Record<string, unknown>)[field] = nextCopy[field];
        }
    }
    return next;
}

/** Prefer persisted child form_language; else fall back to shop/UI locale. */
export function resolveWidgetFormLocale(
    values: Record<string, unknown>,
    fallbackLocale = "en"
): string {
    const stored = values[WIDGET_FORM_LANGUAGE_KEY];
    if (typeof stored === "string" && stored.trim() !== "") {
        return stored.trim();
    }
    return fallbackLocale || "en";
}

/** Test helper — do not use in app code. */
export function _resetWidgetFormCopyCache(): void {
    copyCache.clear();
    stockSeen.clear();
}
